import React, { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { withStyles } from "@mui/styles";
import Info from "@mui/icons-material/Info";

import { withState } from "../../../services/state";
import {
  getAvailabilityRequests as apiGetAvailabilityRequests,
  getAvailabilityRequest as apiGetAvailabilityRequest,
  deleteAvailabilityRequest as apiDeleteAvailabilityRequest,
  convertAvailabilityRequest as apiConvertAvailabilityRequest,
} from "../../../services/api";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import YesNoDialogModal from "../../components/yesNoDialogModal/YesNoDialogModal";
import ConvertAvailabilityRequestDialog from "../../components/convertAvailabilityRequestDialog/ConvertAvailabilityRequestDialog";
import Section from "../../components/section/Section";
import AvailabilityRequest from "../../components/availabilityRequest/AvailabilityRequest";
import Button from "../../components/button/Button";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  eventHeader: {
    backgroundColor: "#c13535",
    color: "#fff",
    padding: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  infoClick: {
    marginLeft: 5,
    cursor: "pointer",
  },
  eventsButtons: {
    padding: 20,
    textAlign: "right",
  },
});

// const event_info = {
//   player:
//     "This is a list of all events you've been added to and need to pay for. To pay for an unpaid event, simply tap an event to view full team details.",
// };

const no_requests = {
  player:
    "There are no requests at the moment. We will send you an email when you have been selected for a request you need to respond to.",
};

function AdminAvailabilityPage({ classes, dispatch, context }) {
  const [gettingAvailabilityRequests, setGettingAvailabilityRequests] =
    React.useState(false);
  const [availabilityRequests, setAvailabilityRequests] = React.useState([]);
  const [showConvertAvailabilityRequest, setConvertAvailabilityRequest] =
    React.useState(null);
  const [convertAvailabilityRequestData, setConvertAvailabilityRequestData] =
    React.useState(null);
  const [loadingConvertData, setLoadingConvertData] = React.useState(false);
  const [convertingAvailabilityRequest, setConvertingAvailabilityRequest] =
    React.useState(false);
  const [convertAvailabilityRequestError, setConvertAvailabilityRequestError] =
    React.useState("");
  const [showDeleteAvailabilityRequest, setDeleteAvailabilityRequest] =
    React.useState(null);
  const [deletingAvailabilityRequest, setDeletingAvailabilityRequest] =
    React.useState(false);
  const [deleteAvailabilityRequestError, setDeleteAvailabilityRequestError] =
    React.useState("");

  const getAvailabilityRequests = useCallback(async () => {
    if (
      !context.currentClub ||
      !context.currentClub.clubId ||
      !context.currentPlayer ||
      !context.currentPlayer.id
    ) {
      return false;
    }

    try {
      setGettingAvailabilityRequests(true);
      const data = await apiGetAvailabilityRequests(
        context.currentClub.clubId,
        context.currentPlayer.id,
      );
      if (data && !data.reason) {
        setAvailabilityRequests(data);
      }
      setGettingAvailabilityRequests(false);
    } catch (e) {
      console.log(e);
      setGettingAvailabilityRequests(false);
    }
  }, [context.currentClub, context.currentPlayer]);

  const deleteAvailabilityRequest = async () => {
    try {
      setDeletingAvailabilityRequest(true);
      await apiDeleteAvailabilityRequest(showDeleteAvailabilityRequest.id);
      setDeletingAvailabilityRequest(false);
      setDeleteAvailabilityRequest(null);
      getAvailabilityRequests();
    } catch (e) {
      setDeletingAvailabilityRequest(false);
      setDeleteAvailabilityRequestError(e.message);
    }
  };

  const openConvertDialog = async (request) => {
    setConvertAvailabilityRequest(request);
    setConvertAvailabilityRequestError("");
    setLoadingConvertData(true);
    try {
      const fullRequest = await apiGetAvailabilityRequest(request.id);
      setConvertAvailabilityRequestData(fullRequest);
    } catch (e) {
      console.log(e);
      setConvertAvailabilityRequestError("Failed to load player data");
    }
    setLoadingConvertData(false);
  };

  const convertAvailabilityRequest = async (playerIds) => {
    try {
      setConvertingAvailabilityRequest(true);
      await apiConvertAvailabilityRequest(showConvertAvailabilityRequest.id, playerIds);
      setConvertingAvailabilityRequest(null);
      setConvertAvailabilityRequest(null);
      getAvailabilityRequests();
    } catch (e) {
      console.log(e);
      setConvertingAvailabilityRequest(false);
      setConvertAvailabilityRequestError(e.message);
    }
  };

  const outstandingAvailabilityRequests = availabilityRequests
    ? availabilityRequests.filter((availabilityRequest) => {
        return (
          !availabilityRequest.haveAllPlayersResponded &&
          (context.currentClub.isTreasurer ||
            availabilityRequest.isCaptainForEventTeam)
        );
      })
    : [];

  const completeAvailabilityRequests = availabilityRequests
    ? availabilityRequests.filter((availabilityRequest) => {
        return (
          availabilityRequest.haveAllPlayersResponded &&
          (context.currentClub.isTreasurer ||
            availabilityRequest.isCaptainForEventTeam)
        );
      })
    : [];

  const renderOutstandingRequests = () => {
    if (
      outstandingAvailabilityRequests &&
      outstandingAvailabilityRequests.length > 0
    ) {
      return (
        <div>
          <AvailabilityRequestHeader
            title="Outstanding Requests"
            backgroundColor="#c37b15"
          />
          <div>
            {outstandingAvailabilityRequests.map(
              (failedPaymentAvailabilityRequest) => {
                let actionButton = null;

                if (failedPaymentAvailabilityRequest.haveAnyPlayersResponded) {
                  actionButton = (
                    <Button
                      onClick={() =>
                        openConvertDialog(
                          failedPaymentAvailabilityRequest,
                        )
                      }
                      title="Convert"
                    />
                  );
                } else if (
                  !failedPaymentAvailabilityRequest.haveAnyPlayersResponded
                ) {
                  actionButton = (
                    <Button
                      onClick={() =>
                        setDeleteAvailabilityRequest(
                          failedPaymentAvailabilityRequest,
                        )
                      }
                      title="Remove"
                    />
                  );
                }

                // Get unread admin messages count from the availability request
                const unreadAdminMessages =
                  failedPaymentAvailabilityRequest.currentAvailabilityRequestPlayer
                    ?.unreadMessageCount || 0;

                return (
                  <AvailabilityRequest
                    key={failedPaymentAvailabilityRequest.id}
                    to={`/availability-requests/${failedPaymentAvailabilityRequest.id}`}
                    availabilityRequest={failedPaymentAvailabilityRequest}
                    actionButton={actionButton}
                    unreadMessageCount={unreadAdminMessages}
                    respondedCount={failedPaymentAvailabilityRequest.respondedCount}
                    totalCount={failedPaymentAvailabilityRequest.totalCount}
                  />
                );
              },
            )}
          </div>
        </div>
      );
    }
  };

  const renderCompleteAvailabilityRequests = () => {
    if (
      completeAvailabilityRequests &&
      completeAvailabilityRequests.length > 0
    ) {
      return (
        <div>
          <AvailabilityRequestHeader
            title="All Players Responded"
            backgroundColor="#07843e"
          />
          <div>
            {completeAvailabilityRequests.map((paidAvailabilityRequest) => {
              let actionButton = null;

              if (paidAvailabilityRequest.haveAnyPlayersResponded) {
                actionButton = (
                  <Button
                    onClick={() =>
                      openConvertDialog(paidAvailabilityRequest)
                    }
                    title="Convert"
                  />
                );
              }

              // Get unread admin messages count from the availability request
              const unreadAdminMessages =
                paidAvailabilityRequest.currentAvailabilityRequestPlayer
                  ?.unreadMessageCount || 0;

              return (
                <AvailabilityRequest
                  key={paidAvailabilityRequest.id}
                  to={`/availability-requests/${paidAvailabilityRequest.id}`}
                  availabilityRequest={paidAvailabilityRequest}
                  actionButton={actionButton}
                  unreadMessageCount={unreadAdminMessages}
                  respondedCount={paidAvailabilityRequest.respondedCount}
                  totalCount={paidAvailabilityRequest.totalCount}
                />
              );
            })}
          </div>
        </div>
      );
    }
  };

  useEffect(() => {
    getAvailabilityRequests();
  }, [getAvailabilityRequests]);

  return (
    <PageWrapper
      showHeader={true}
      showSidebar={true}
      title="Admin Availability"
      // onInfoClick={
      //   event_info["player"]
      //     ? () => setShowAvailabilityRequestsInfo(true)
      //     : null
      // }
      rightContent={
        <Link to="/availability-requests/new">
          <Button title="Create" />
        </Link>
      }
    >
      <Section style={{ padding: 0, backgroundColor: "transparent" }}>
        <div>
          {renderOutstandingRequests()}
          {renderCompleteAvailabilityRequests()}
          {!gettingAvailabilityRequests &&
            availabilityRequests.length === 0 && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                {no_requests["player"]}
              </div>
            )}
        </div>
      </Section>

      {showDeleteAvailabilityRequest && (
        <YesNoDialogModal
          title="Are you sure you want to delete this availability request?"
          onClose={() => setDeleteAvailabilityRequest(false)}
          onSubmit={() => deleteAvailabilityRequest()}
          submitting={deletingAvailabilityRequest}
          error={deleteAvailabilityRequestError}
        />
      )}

      {showConvertAvailabilityRequest && (
        <ConvertAvailabilityRequestDialog
          availabilityRequest={convertAvailabilityRequestData}
          loading={loadingConvertData}
          onClose={() => {
            setConvertAvailabilityRequest(null);
            setConvertAvailabilityRequestData(null);
          }}
          onConfirm={(playerIds) => convertAvailabilityRequest(playerIds)}
          submitting={convertingAvailabilityRequest}
          error={convertAvailabilityRequestError}
        />
      )}
    </PageWrapper>
  );
}

const AvailabilityRequestHeader = withStyles(styles)(({
  title,
  classes,
  onInfoClick,
  backgroundColor,
}) => {
  return (
    <div
      className={classes.eventHeader}
      style={{ backgroundColor: backgroundColor || "transparent" }}
    >
      {title}
      {onInfoClick && (
        <div className={classes.infoClick} onClick={() => onInfoClick()}>
          <Info />
        </div>
      )}
    </div>
  );
});

export default withStyles(styles)(withState(AdminAvailabilityPage));
